import React from 'react'
import dynamic from 'next/dynamic'

import { CONTENT_TYPE } from 'contentful/types/contentful-modules'
import { RootSectionReactTypes } from './contentMapper'
import type { SideBySideModuleProps } from '@/components/side-by-side/side-by-side.module'
import type { CarouselModuleProps } from '@/components/carousel/carousel.module'
import type { SeoLinksModuleProps } from '@/components/seo-links/seo-links.module'
import type { SeoTextModuleProps } from '@/components/RichText/seo-text.module'
import type { ImageModuleProps } from '@/components/image/image.module'
import type { PodsModuleProps } from '@/components/pods-module/pods.module'
import type { NewsletterModuleProps } from '@/components/newsletter/newsletter.module'
import type { TextModuleProps } from '@/components/RichText/text.module'
import type { BlogHeaderModuleProps } from '@/components/blog-header/blog-header.module'
import type { GetAppModuleProps } from '@/components/GetApp/GetApp'
import {
  ExpandableTextModule,
  ExpandableTextModuleProps,
} from '@/components/expandable-text/expandable-text.module'
import { CountdownBannerProps } from '@/components/CountdownBanner/CountdownBanner'

import { TaggedArticlesModuleProps } from '@/components/tagged-articles/tagged-articles.module'
import {
  BlogTagListModule,
  BlogTagListModuleProps,
} from '@/components/BlogTags/BlogTagListModule'
import {
  PageTitleLowExperiment,
  PageTitleLowExperimentProps,
} from '@/components/page-title-low/page-title-low.experiment'
import { withCtfassetsUrlFix } from '@/components/HOC/withCtfassetsUrlFix'

const SideBySideModule = dynamic(
  () =>
    import('@/components/side-by-side/side-by-side.module').then((mod) =>
      withCtfassetsUrlFix(mod.SideBySideModule),
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const CarouselModule = dynamic(
  () =>
    import('@/components/carousel/carousel.module').then(
      (mod) => mod.CarouselModule,
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const SeoLinksModule = dynamic(
  () =>
    import('@/components/seo-links/seo-links.module').then(
      (mod) => mod.SeoLinksModule,
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const SeoTextModule = dynamic(
  () =>
    import('@/components/RichText/seo-text.module').then(
      (mod) => mod.SeoTextModule,
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const TextModule = dynamic(
  () =>
    import('@/components/RichText/text.module').then((mod) => mod.TextModule),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const ImageModule = dynamic(
  () =>
    import('@/components/image/image.module').then((mod) =>
      withCtfassetsUrlFix(mod.ImageModule),
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const PodsModule = dynamic(
  () =>
    import('@/components/pods-module/pods.module').then((mod) =>
      withCtfassetsUrlFix(mod.PodsModule),
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)

const NewsLetterModule = dynamic(
  () =>
    import('@/components/newsletter/newsletter.module').then((mod) =>
      withCtfassetsUrlFix(mod.NewsLetterModule),
    ),
  {
    ssr: false,
    loading: () => <></>,
  },
)

const CountDownBannerModule = dynamic(
  () =>
    import('@/components/CountdownBanner/CountdownBanner').then((mod) =>
      withCtfassetsUrlFix(mod.CountdownBanner),
    ),
  {
    ssr: false,
    loading: () => <></>,
  },
)
const BlogHeaderModule = dynamic(
  () =>
    import('@/components/blog-header/blog-header.module').then((mod) =>
      withCtfassetsUrlFix(mod.BlogHeaderModule),
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)
const TaggedArticlesModule = dynamic(
  () =>
    import('@/components/tagged-articles/tagged-articles.module').then((mod) =>
      withCtfassetsUrlFix(mod.TaggedArticlesModule),
    ),
  {
    ssr: true,
    loading: () => <></>,
  },
)
const GetAppModule = dynamic(
  () => import('@/components/GetApp/GetApp').then((mod) => mod.GetApp),
  {
    ssr: true,
    loading: () => <></>,
  },
)

export const getContentModule = ({
  contentType,
  content,
}: {
  contentType: CONTENT_TYPE | 'page-title-low.experiment'
  content: RootSectionReactTypes
}): React.ReactElement | null => {
  switch (contentType) {
    case 'side-by-side.module':
      if (!(content as SideBySideModuleProps).headline) {
        return null
      }
      return <SideBySideModule {...(content as SideBySideModuleProps)} />
    case 'carousel.module':
      return <CarouselModule {...(content as CarouselModuleProps)} />
    case 'seo-links.module':
      return <SeoLinksModule {...(content as SeoLinksModuleProps)} />
    case 'seo-text.module':
      return <SeoTextModule {...(content as SeoTextModuleProps)} />
    case 'text.module':
      if (!(content as TextModuleProps).text) {
        return null
      }
      return <TextModule {...(content as TextModuleProps)} />
    case 'pods.module':
      return <PodsModule {...(content as PodsModuleProps)} />
    case 'image.module':
      return <ImageModule {...(content as ImageModuleProps)} />
    case 'newsletter.module':
      return <NewsLetterModule {...(content as NewsletterModuleProps)} />
    case 'expandable-text.module':
      return (
        <ExpandableTextModule {...(content as ExpandableTextModuleProps)} />
      )
    case 'countdownBanner.module':
      return <CountDownBannerModule {...(content as CountdownBannerProps)} />
    case 'blog-header.module':
      return <BlogHeaderModule {...(content as BlogHeaderModuleProps)} />
    case 'blog-tag.module':
      return null
    case 'tagged-articles.module':
      return (
        <TaggedArticlesModule {...(content as TaggedArticlesModuleProps)} />
      )
    case 'blog-tag-list.module':
      return <BlogTagListModule {...(content as BlogTagListModuleProps)} />
    case 'get-app.module':
      return <GetAppModule {...(content as GetAppModuleProps)} />
    case 'page-title-low.experiment':
      return (
        <PageTitleLowExperiment {...(content as PageTitleLowExperimentProps)} />
      )
  }
  throw new Error(`Unknown content type ${contentType}`)
}
